// frontend/src/api.js
const BASE = "/api";

// ===== Helper: Handle JSON responses =====
async function handle(res) {
  if (!res.ok) {
    const text = await res.text();
    const error = new Error(text || res.statusText);
    
    // Try to parse JSON error response
    try {
      const errorData = JSON.parse(text);
      error.response = { data: errorData };
    } catch (e) {
      // If not JSON, just use the text
      error.response = { data: { message: text } };
    }
    
    throw error;
  }
  return res.json();
}

/**
 * What the server actually said, for showing to a person.
 *
 * DRF answers a refused request with `{detail: "..."}` and a validation error
 * with `{field: ["..."]}`. Both carry the one thing the user needs. Swallowing
 * them behind a fixed string is how "QuickBooks refused this because client
 * BROST JAMIE is not mapped to a customer" reached Lee as "Failed."
 *
 * `fallback` is used only when the server said nothing usable.
 */
export function apiErrorMessage(error, fallback = "Something went wrong.") {
  const data = error?.response?.data;

  // DRF refuses a request with {detail: "..."} -- the single most useful thing
  // the server can say.
  if (data && typeof data === "object" && typeof data.detail === "string" && data.detail.trim()) {
    return data.detail.trim();
  }

  // A validation error is {field: ["..."], ...}.
  if (data && typeof data === "object") {
    const messages = Object.entries(data)
      // `message` is what handle() stuffs a NON-JSON body into; it is raw
      // response text, not a field error, so it is considered separately below.
      .filter(([key]) => key !== "message")
      .map(([, value]) => value)
      .flat()
      .filter((m) => typeof m === "string" && m.trim());
    if (messages.length) return messages.join(" ");
  }

  // A non-JSON body. Worth showing only if it reads like a sentence: a bare
  // status line ("Internal Server Error") or an empty JSON body tells the user
  // nothing they can act on, and is worse than the caller's own wording.
  const raw = (typeof data?.message === "string" ? data.message : "").trim();
  const looksLikeAStatusLine = /^\(?\d{0,3}\)?[\s-]*[A-Za-z ]{0,40}$/.test(raw);
  if (raw && raw.length < 300 && !looksLikeAStatusLine && /[a-z]{3}/i.test(raw)) {
    return raw;
  }
  return fallback;
}

// ===== Helper function to get CSRF token from cookie =====
function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.substring(0, name.length + 1) === (name + '=')) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

// ===== AUTH =====
export async function authMe() {
  const res = await fetch(`${BASE}/auth/me/`, { credentials: "include" });
  if (!res.ok) throw new Error("Not authenticated");
  return res.json();
}

export async function authLogin(username, password) {
  const csrfToken = getCookie('csrftoken');
  
  const res = await fetch(`${BASE}/auth/login/`, {
    method: "POST",
    credentials: "include",
    headers: { 
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    body: JSON.stringify({ username, password }),
  });
  return handle(res);
}

export async function authLogout() {
  const csrfToken = getCookie('csrftoken');
  
  const res = await fetch(`${BASE}/auth/logout/`, {
    method: "POST",
    credentials: "include",
    headers: {
      "X-CSRFToken": csrfToken,
    },
  });
  return handle(res);
}

// ===== GENERIC CRUD HELPERS =====
export async function apiGet(path) {
  // Ensure path starts with /api if it's a relative path
  const url = path.startsWith('http') ? path : (path.startsWith('/api') ? path : `${BASE}${path}`);
  const res = await fetch(url, { credentials: "include" });
  return handle(res);
}

export async function apiPost(path, body) {
  const csrfToken = getCookie('csrftoken');
  const url = path.startsWith('http') ? path : (path.startsWith('/api') ? path : `${BASE}${path}`);
  
  const res = await fetch(url, {
    method: "POST",
    credentials: "include",
    headers: { 
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    body: JSON.stringify(body),
  });
  return handle(res);
}

export async function apiPatch(path, body) {
  const csrfToken = getCookie('csrftoken');
  const url = path.startsWith('http') ? path : (path.startsWith('/api') ? path : `${BASE}${path}`);
  
  const res = await fetch(url, {
    method: "PATCH",
    credentials: "include",
    headers: { 
      "Content-Type": "application/json",
      "X-CSRFToken": csrfToken,
    },
    body: JSON.stringify(body),
  });
  return handle(res);
}

export async function apiDelete(path) {
  const csrfToken = getCookie('csrftoken');
  const url = path.startsWith('http') ? path : (path.startsWith('/api') ? path : `${BASE}${path}`);
  
  const res = await fetch(url, {
    method: "DELETE",
    credentials: "include",
    headers: {
      "X-CSRFToken": csrfToken,
    },
  });
  if (!res.ok) throw new Error(await res.text());
  return true;
}

// Fetch all pages of paginated results.
//
// The first page is fetched to learn DRF's `count`; any remaining pages are
// then fetched CONCURRENTLY (not chained on `next`) so total wall-time is ~one
// round-trip regardless of page count. Return shape is unchanged: a flat array
// for paginated/array endpoints, or the raw object for single-object responses.
export async function apiGetAll(path) {
  // Normalize to a same-origin path+query (Django can emit http:// next links).
  const toLocal = (u) => {
    if (typeof u === 'string' && u.startsWith('http')) {
      try { const p = new URL(u); return p.pathname + p.search; } catch (e) { /* use as-is */ }
    }
    return u;
  };

  const firstUrl = path.startsWith('http')
    ? toLocal(path)
    : (path.startsWith('/api') ? path : `${BASE}${path}`);

  const first = await handle(await fetch(firstUrl, { credentials: 'include' }));

  // Non-paginated shapes: return as-is (unchanged behavior).
  if (Array.isArray(first)) return first;
  if (!first || !Array.isArray(first.results)) return first;

  const results = [...first.results];
  const pageSize = first.results.length;
  const total = typeof first.count === 'number' ? first.count : results.length;

  // Only one page's worth of data — done.
  if (pageSize === 0 || results.length >= total) return results;

  // Build the remaining page URLs from the first `next` link, swapping page=N.
  const nextLocal = toLocal(first.next);
  if (!nextLocal) return results;
  const totalPages = Math.ceil(total / pageSize);

  const buildPageUrl = (pageNum) => {
    // nextLocal already points at page 2; derive siblings by replacing page=.
    if (/[?&]page=/.test(nextLocal)) {
      return nextLocal.replace(/([?&]page=)\d+/, `$1${pageNum}`);
    }
    const sep = nextLocal.includes('?') ? '&' : '?';
    return `${nextLocal}${sep}page=${pageNum}`;
  };

  // Cap concurrency so a large firm doesn't fire dozens of requests at once.
  const CONCURRENCY = 8;
  const pageNums = [];
  for (let p = 2; p <= totalPages; p++) pageNums.push(p);

  const pages = new Array(pageNums.length);
  let cursor = 0;
  async function worker() {
    while (cursor < pageNums.length) {
      const idx = cursor++;
      const data = await handle(await fetch(buildPageUrl(pageNums[idx]), { credentials: 'include' }));
      pages[idx] = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
    }
  }
  await Promise.all(Array.from({ length: Math.min(CONCURRENCY, pageNums.length) }, worker));

  for (const chunk of pages) results.push(...chunk);
  return results;
}

// --- Convenience wrappers used across old pages ---

export function listify(obj) {
  // Handle DRF paginated responses
  if (obj && obj.results && Array.isArray(obj.results)) {
    return obj.results;
  }
  // Handle plain arrays
  if (Array.isArray(obj)) {
    return obj;
  }
  // Handle empty/null
  if (!obj) {
    return [];
  }
  // Handle object-to-list conversion (old format)
  return Object.entries(obj).map(([k, v]) => ({ id: k, name: v }));
}

// --- Notifications ---
export async function getNotifications() {
  return apiGet("/api/notifications/");
}
export async function getUnreadCount() {
  return apiGet("/api/notifications/unread_count/");
}
export async function markNotificationRead(id) {
  return apiPost(`/api/notifications/${id}/mark_read/`, {});
}
export async function markAllNotificationsRead() {
  return apiPost("/api/notifications/mark_all_read/", {});
}

// --- Time-code helpers used by TimeCodesPage / TimeEntryPage ---
export async function getTimeCodes() {
  return apiGet(`${BASE}/time-codes/`);
}
export async function createTimeCode(body) {
  return apiPost(`${BASE}/time-codes/`, body);
}
export async function updateTimeCode(id, body) {
  return apiPatch(`${BASE}/time-codes/${id}/`, body);
}
export async function deleteTimeCode(id) {
  return apiDelete(`${BASE}/time-codes/${id}/`);
}

export async function apiPut(path, body) {
  const csrfToken = getCookie('csrftoken');
  const url = path.startsWith('http') ? path : (path.startsWith('/api') ? path : `/api${path}`);
  
  const res = await fetch(url, {
    method: 'PUT',
    credentials: 'include',
    headers: { 
      'Content-Type': 'application/json',
      'X-CSRFToken': csrfToken,
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(`API error: ${res.status}`);
  return res.json();
}

// Replaces the caller's pinned nav items. The server derives the row from
// the session, so there is no user id to pass.
export const saveNavPins = (pins) => apiPut("/api/nav/pins/", { pins });

// --- Client name picker used in TimeEntryPage ---
export function pickClientName(clients, id) {
  if (!id) return "—";
  const match = clients.find((c) => String(c.id) === String(id));
  return match ? match.name : "—";
}

export const getClientUpdates = (clientId) =>
  apiGet(`/api/client-updates/by_client/${clientId}/`);

export const createClientUpdate = (data) =>
  apiPost('/api/client-updates/', data);

export const deleteClientUpdate = (id) =>
  apiDelete(`/api/client-updates/${id}/`);

// --- Clients ---
export const createClient = (data) => apiPost("/api/clients/", data);

// ===== OTHER TASKS =====
export const getOtherTasks = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiGetAll(`/api/other-tasks/${qs ? `?${qs}` : ""}`);
};
 
export const getOtherTask = (id) => apiGet(`/api/other-tasks/${id}/`);
 
export const createOtherTask = (data) => apiPost(`/api/other-tasks/`, data);
 
export const updateOtherTask = (id, data) =>
  apiPatch(`/api/other-tasks/${id}/`, data);
 
export const deleteOtherTask = (id) => apiDelete(`/api/other-tasks/${id}/`);
 
// Employee assignment / unassignment on a task
export const assignTaskEmployee = (taskId, employeeId) =>
  apiPost(`/api/other-tasks/${taskId}/assign_employee/`, {
    employee_id: employeeId,
  });
 
export const unassignTaskEmployee = (taskId, employeeId) =>
  apiDelete(`/api/other-tasks/${taskId}/unassign_employee/${employeeId}/`);
 
// ===== OTHER TASK TYPES =====
export const getOtherTaskTypes = () => apiGetAll(`/api/other-task-types/`);
 
export const createOtherTaskType = (data) =>
  apiPost(`/api/other-task-types/`, data);
 
// ===== OTHER TASK FILES (SharePoint) =====
export const attachSharePointToTask = (taskId, fileMetadata) =>
  apiPost(`/api/sharepoint/tasks/files/attach/`, {
    task_id: taskId,
    file_metadata: fileMetadata,
  });

export const unlinkSharePointFromTask = (fileId) =>
  apiDelete(`/api/sharepoint/tasks/files/${fileId}/unlink/`);

// ===== TASKS =====
export const getTasks = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiGetAll(`/api/tasks/${qs ? `?${qs}` : ""}`);
};

export const getTask = (id) => apiGet(`/api/tasks/${id}/`);

export const createTask = (data) => apiPost(`/api/tasks/`, data);

export const updateTask = (id, data) => apiPatch(`/api/tasks/${id}/`, data);

export const deleteTask = (id) => apiDelete(`/api/tasks/${id}/`);

// body: { overrides?, archive_source? } -- see TaskViewSet.roll_forward.
export const rollForwardTask = (id, body = {}) =>
  apiPost(`/api/tasks/${id}/roll-forward/`, body);

// Whether the task's fiscal year still takes time: { state: open | pending |
// locked, locks_at }. POSTing locks it now -- see TaskViewSet.time_lock.
export const getTaskTimeLock = (id) => apiGet(`/api/tasks/${id}/time-lock/`);
export const lockTaskTime = (id) => apiPost(`/api/tasks/${id}/time-lock/`, {});

// What rollForwardTask would create, without creating it.
export const getRollForwardProposal = (id) =>
  apiGet(`/api/tasks/${id}/roll-forward/`);

export const getRollForwardPolicies = () =>
  apiGet("/api/roll-forward/policies/");

export const updateRollForwardPolicy = (category, patch) =>
  apiPatch(`/api/roll-forward/policies/${category}/`, patch);

export const previewRollForward = (scope) =>
  apiPost("/api/roll-forward/preview/", scope);

export const runRollForward = (body) =>
  apiPost("/api/roll-forward/run/", body);

export const undoRollForwardBatch = (id) =>
  apiPost(`/api/roll-forward/batches/${id}/undo/`, {});

export const getRollForwardReady = () =>
  apiGet("/api/roll-forward/ready/");

// ============================================================================
// Shared query-string helper. Drops undefined/null/empty-string params but
// keeps legitimate zeros (e.g. months=0, planned_only=0).
// ============================================================================
const queryString = (params = {}) => {
  const qs = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") qs.append(k, v);
  });
  return qs.toString();
};

// ============================================================================
// Planning (Part 2). The grid endpoint returns aggregate cells only; task
// rows come from the cell endpoint, one cell at a time.
// ============================================================================
export const getPlanningGrid = (params = {}) =>
  apiGet(`/api/planning/grid/?${queryString(params)}`);

export const getPlanningCell = (params = {}) =>
  apiGet(`/api/planning/cell/?${queryString(params)}`);

export const assignPlanning = (body) =>
  apiPost("/api/planning/assign/", body);

// Which invoice is the client's most recent -- a LIST row, never a whole
// invoice. The list action serialises with InvoiceListSerializer, whose
// get_fields() pops "lines" (invoicing/serializers.py), so a row from here
// carries no lines at all. It can identify an invoice; it cannot be read as
// one. Callers below turn the winning row into a full record via /<id>/.
//
// `ordering=-date` cannot break a same-day tie (id is not in the viewset's
// ordering_fields), so the tie is broken here toward the higher id -- the one
// entered last.
//
// The last invoice that actually WENT OUT, not the newest draft: a draft
// somebody started and left would otherwise become "last time's bill". Only a
// client with nothing but drafts falls back to the newest draft.
async function lastInvoiceRowForClient(clientId) {
  if (!clientId) return null;
  let rows = listify(
    await apiGet(`/api/invoices/?client=${clientId}&ordering=-date&exclude_status=DRAFT`),
  );
  if (!rows.length) {
    rows = listify(await apiGet(`/api/invoices/?client=${clientId}&ordering=-date`));
  }
  if (!rows.length) return null;
  const newest = rows[0].date;
  return rows
    .filter((r) => r.date === newest)
    .sort((a, b) => b.id - a.id)[0];
}

// The full detail record for an invoice id. The detail action keeps
// InvoiceSerializer, so this -- and only this -- has `lines`.
const getInvoiceDetail = (id) => apiGet(`/api/invoices/${id}/`);

// The client's most recent ISSUED invoice (dated on or before `onOrBefore`,
// when given), in full, or null. Never a draft, never the invoice being edited.
// What a draft saved without a prefill snapshot is compared with for its
// yellow marks; Create Invoice passes no date, so it is the client's last
// issued invoice whatever its date.
export async function getLastIssuedInvoiceBefore(clientId, { onOrBefore, excludeId } = {}) {
  if (!clientId) return null;
  const qs = new URLSearchParams({ client: clientId, ordering: "-date", exclude_status: "DRAFT" });
  if (onOrBefore) qs.set("end_date", onOrBefore);
  const rows = listify(await apiGet(`/api/invoices/?${qs}`))
    .filter((r) => String(r.id) !== String(excludeId) && r.status !== "DRAFT");
  if (!rows.length) return null;
  const newest = rows.reduce((a, b) => (b.date > a.date || (b.date === a.date && b.id > a.id) ? b : a));
  return getInvoiceDetail(newest.id);
}

// Every DRAFT invoice the client already has (list rows: no lines), newest
// first. Create Invoice asks before a second draft is started for a client.
export async function getDraftInvoicesForClient(clientId) {
  if (!clientId) return [];
  const rows = listify(await apiGet(`/api/invoices/?client=${clientId}&status=DRAFT`));
  return rows
    .filter((r) => String(r.status || "").toUpperCase() === "DRAFT" &&
      (r.client === undefined || String(r.client) === String(clientId)))
    .sort((a, b) => (a.date === b.date ? b.id - a.id : a.date < b.date ? 1 : -1));
}

// The client's most recent invoice, in full, or null.
export async function getLastInvoiceForClient(clientId) {
  const row = await lastInvoiceRowForClient(clientId);
  if (!row) return null;
  return getInvoiceDetail(row.id);
}

// The newest invoice belonging to ANY other client in the group, or null.
// Only ever called when the client has no invoice of their own, so the extra
// requests are paid once, for a client being billed for the first time.
//
// The members are searched with the cheap list rows and the winner alone is
// then fetched in full: one detail request for the group, not one per member.
export async function getLastInvoiceForGroup(groupId, excludeClientId) {
  if (!groupId) return null;
  const members = await getClientsInGroup(groupId);
  const others = members.filter((c) => String(c.id) !== String(excludeClientId));
  const found = (await Promise.all(
    others.map((c) =>
      lastInvoiceRowForClient(c.id)
        .then((row) => (row ? { ...row, _sourceClientName: c.name } : null))
        .catch(() => null),
    ),
  )).filter(Boolean);
  if (!found.length) return null;
  const winner = found.sort((a, b) =>
    a.date === b.date ? b.id - a.id : (a.date < b.date ? 1 : -1),
  )[0];
  const detail = await getInvoiceDetail(winner.id);
  // The name rides on the detail record, not the row: the banner has to say
  // whose invoice this was, and the detail payload does not know.
  return { ...detail, _sourceClientName: winner._sourceClientName };
}

// Deadlines and Triage read the same work through the same scoping helpers
// the grid does -- same params, so the three views cannot disagree about
// what is in scope.
export const getPlanningDeadlines = (params = {}) =>
  apiGet(`/api/planning/deadlines/?${queryString(params)}`);

// Counts always; one bucket's rows only when `bucket` asks for them. The
// three counts overlap by design and are never summed -- see TriageView.
export const getPlanningTriage = (params = {}) =>
  apiGet(`/api/planning/triage/?${queryString(params)}`);

// ============================================================================
// Review (Part 3). Aggregate stage counts per cohort -- no task rows.
// ============================================================================
export const getReviewFunnel = (params = {}) =>
  apiGet(`/api/review/funnel/?${queryString(params)}`);

// ============================================================================
// Archiving (Phase 4). Archiving is reversible — restore() undoes archive().
// ============================================================================
export const getArchivedTasks = (params = {}) => {
  const qs = new URLSearchParams({ ...params, archived: 1 }).toString();
  return apiGetAll(`/api/tasks/?${qs}`);
};

export const archiveTask = (id) => apiPost(`/api/tasks/${id}/archive/`, {});

export const restoreTask = (id) => apiPost(`/api/tasks/${id}/restore/`, {});

// ============================================================================
// Audit trail / timeline (Phase 4)
// ============================================================================
// recordType: 'task' | 'client' | 'invoice' | 'timeentry'
export const getAuditLog = (recordType, recordId) =>
  apiGet(`/api/audit-log/?record_type=${recordType}&record_id=${recordId}`);

// ============================================================================
// Task files (per-task SharePoint links and uploads)
// ============================================================================
export const getTaskFiles = (taskId) =>
  apiGetAll(`/api/task-files/?task=${taskId}`);
export const createTaskFile = (data) => apiPost(`/api/task-files/`, data);
export const deleteTaskFile = (id) => apiDelete(`/api/task-files/${id}/`);

// ============================================================================
// Time entries
// ============================================================================
export const getTimeEntriesForTask = (taskId) =>
  apiGetAll(`/api/time-entries/?task=${taskId}`);

export const getTimeEntries = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiGetAll(`/api/time-entries/${qs ? `?${qs}` : ""}`);
};
export const createTimeEntry = (data) => apiPost("/api/time-entries/", data);
export const updateTimeEntry = (id, data) =>
  apiPatch(`/api/time-entries/${id}/`, data);
export const deleteTimeEntry = (id) => apiDelete(`/api/time-entries/${id}/`);

export const submitWeek = (weekStart, createdBy) =>
  apiPost("/api/time-entries/submit_week/", {
    week_start: weekStart,
    ...(createdBy ? { created_by: createdBy } : {}),
  });
// One day's lines, same rules as submitWeek.
export const submitDay = (date, createdBy) =>
  apiPost("/api/time-entries/submit_day/", {
    date,
    ...(createdBy ? { created_by: createdBy } : {}),
  });
export const approveWeek = (weekStart, employeeId) =>
  apiPost("/api/time-entries/approve_week/", {
    week_start: weekStart,
    employee_id: employeeId,
  });
export const rejectWeek = (weekStart, employeeId, note) =>
  apiPost("/api/time-entries/reject_week/", {
    week_start: weekStart,
    employee_id: employeeId,
    note,
  });
export const approveEntry = (id) =>
  apiPost(`/api/time-entries/${id}/approve/`, {});
export const rejectEntry = (id, note) =>
  apiPost(`/api/time-entries/${id}/reject/`, { note });

export const getApprovalQueue = (params = {}) => {
  const clean = Object.fromEntries(
    Object.entries(params).filter(([, v]) => v != null && v !== "")
  );
  const qs = new URLSearchParams(clean).toString();
  return apiGet(`/api/time-entries/approval_queue/${qs ? `?${qs}` : ""}`);
};

// Mirrors the backend's `if employee_ids is not None`: omitting the key means
// "every employee" (the approve-the-whole-week button), while an explicit empty
// list means "nobody". Collapsing [] into an omission would approve everyone.
export const approveBulk = (startDate, endDate, employeeIds = null) =>
  apiPost("/api/time-entries/approve_bulk/", {
    start_date: startDate,
    end_date: endDate,
    ...(employeeIds != null ? { employee_ids: employeeIds } : {}),
  });

// ============================================================================
// Task Types (templates for corporate/personal Tasks)
// ============================================================================
export const getTaskTypes = () => apiGetAll(`/api/task-types/`);
export const createTaskType = (data) => apiPost(`/api/task-types/`, data);
export const updateTaskType = (id, data) =>
  apiPatch(`/api/task-types/${id}/`, data);
export const deleteTaskType = (id) => apiDelete(`/api/task-types/${id}/`);

// Bulk-assign a TaskType to multiple Tasks (client-side fan-out).
export const bulkAssignTaskType = (taskIds, taskTypeId) =>
  Promise.all(
    taskIds.map((id) => apiPatch(`/api/tasks/${id}/`, { task_type: taskTypeId }))
  );

// ===== CLIENT GROUPS =====
// Groups are real records now, not a list of distinct strings.
export const getClientGroups = () => apiGetAll(`/api/client-groups/`);
export const getClientGroup = (id) => apiGet(`/api/client-groups/${id}/`);
export const getClientsInGroup = (groupId) => apiGetAll(`/api/clients/?group=${groupId}`);

/**
 * A typed group name -> the id of the matching ClientGroup, creating it if needed.
 *
 * Matched case- and whitespace-insensitively, so typing "smith holdings" joins the
 * existing "Smith Holdings" rather than creating a twin of it. Blank means "no
 * group" and resolves to null.
 */
export async function resolveClientGroup(name) {
  // Thin wrapper: the backend is the sole owner of group-name resolution
  // (ClientGroup.resolve, matched on the casefolded normalised key). Do not
  // reimplement any matching/normalisation here — JS .toLowerCase() is not
  // Python .casefold() (e.g. "Straße" vs "Strasse"), so client-side matching
  // can create a group the backend would treat as an exact duplicate.
  if (!String(name ?? '').trim()) return null;
  const group = await apiPost('/api/client-groups/resolve/', { name });
  return group ? group.id : null;
}

// ===== WORKFLOW ITEMS (Task + OtherTask union) =====
export const getWorkflowItems = (params = {}) => {
  const qs = new URLSearchParams(params).toString();
  return apiGetAll(`/api/workflow/items/${qs ? `?${qs}` : ""}`);
};

// ===== STATUTORY HOLIDAYS =====
export const getStatHolidays = (year) =>
  apiGet(`/stat-holidays/${year ? `?year=${year}` : ""}`);

export const saveStatHolidayOverride = (body) =>
  apiPost("/stat-holidays/override/", body);

// apiDelete sends no body, so the year and name go in the query string.
export const revertStatHoliday = (year, name) =>
  apiDelete(`/stat-holidays/override/?year=${year}&name=${encodeURIComponent(name)}`);

// ===== INVOICE DESCRIPTION PRESETS =====
// The standard invoice line wordings, edited in Settings. Unpaginated: a list
// of a couple of dozen rows. `activeOnly` is what the invoice picker wants;
// Settings lists everything, deactivated rows included.
export const getInvoiceDescriptionPresets = ({ activeOnly = false } = {}) =>
  apiGet(`/api/invoice-description-presets/${activeOnly ? "?active=1" : ""}`);

export const createInvoiceDescriptionPreset = (data) =>
  apiPost("/api/invoice-description-presets/", data);

export const updateInvoiceDescriptionPreset = (id, data) =>
  apiPatch(`/api/invoice-description-presets/${id}/`, data);

export const deleteInvoiceDescriptionPreset = (id) =>
  apiDelete(`/api/invoice-description-presets/${id}/`);

// Renumbers one category in a single request: ids in their new order.
export const reorderInvoiceDescriptionPresets = (category, ids) =>
  apiPost("/api/invoice-description-presets/reorder/", { category, ids });
